import { useState } from "react";
import { Badge, Group, Stack, Table, Text, TextInput } from "@mantine/core";
import { IconSearch } from "@tabler/icons-react";
import { useLiveCollection, useLiveDoc } from "./useLive";
import { CtcAdministrationConfig, CtcStaffProfile } from "../../types";
import { ctcClearable } from "./ctc_panel";


/** Staff in the dealer groups (ctc_config/administration), sorted by name. Shared by Back office → Dealers and dealer assignment. */
export const useCtcDealers = () => {
    const { data: config, loading: loadingConfig } = useLiveDoc<CtcAdministrationConfig>("ctc_config/administration");
    const { data: staff, loading: loadingStaff } = useLiveCollection<CtcStaffProfile & { id: string }>("ctc_staff");
    const groupIds = config?.administrationGroupId ?? [];
    const dealers = staff
        .filter((s) => s.groupIds?.some((id) => groupIds.includes(id)))
        .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email));
    return { dealers, groupIds, loading: loadingConfig || loadingStaff };
};

/**
 * Back office → Dealers: CTC staff whose Microsoft groups (mirrored onto ctc_staff/{email} by
 * customClaims at sign-in) include any group in ctc_config/administration.administrationGroupId.
 * The groups are configured in Firestore; this view is read-only. Roles still come from config/authorizationClaims.
 */
const CtcDealers = () => {
    const { data: config, loading: loadingConfig } = useLiveDoc<CtcAdministrationConfig>("ctc_config/administration");
    const { data: staff, loading: loadingStaff } = useLiveCollection<CtcStaffProfile & { id: string }>("ctc_staff");
    const [search, setSearch] = useState("");

    const groupIds = config?.administrationGroupId ?? [];
    const term = search.trim().toLowerCase();

    const dealers = staff
        .filter((s) => s.groupIds?.some((id) => groupIds.includes(id)))
        .filter((s) => !term || [s.displayName, s.email, ...(s.groups ?? []).map((g) => g.displayName)]
            .some((value) => value?.toLowerCase().includes(term)))
        .sort((a, b) => (a.displayName || a.email).localeCompare(b.displayName || b.email));

    const loading = loadingConfig || loadingStaff;

    return (
        <Stack>
            <TextInput
                placeholder="Search by name, email or group"
                leftSection={<IconSearch size={16} />}
                value={search}
                onChange={(e) => setSearch(e.currentTarget.value)}
                {...ctcClearable(search, () => setSearch(""))}
            />

            <Table striped highlightOnHover withTableBorder>
                <Table.Thead>
                    <Table.Tr>
                        <Table.Th>Name</Table.Th>
                        <Table.Th>Email</Table.Th>
                        <Table.Th>Groups</Table.Th>
                        <Table.Th>Signature</Table.Th>
                        <Table.Th>Groups synced</Table.Th>
                    </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                    {dealers.map((s) => (
                        <Table.Tr key={s.id}>
                            <Table.Td>{s.displayName || "—"}</Table.Td>
                            <Table.Td>{s.email}</Table.Td>
                            <Table.Td>
                                <Group gap={4}>
                                    {s.groups?.filter((g) => groupIds.includes(g.id)).map((g) => <Badge key={g.id} variant="light">{g.displayName || g.id}</Badge>)}
                                </Group>
                            </Table.Td>
                            <Table.Td>{s.signaturePath ? "Yes" : <Text span c="orange" size="sm">Missing</Text>}</Table.Td>
                            <Table.Td>{s.groupsSyncedAt?.toDate?.().toLocaleString("en-GB", { timeZone: "Asia/Kuwait" }) ?? "—"}</Table.Td>
                        </Table.Tr>
                    ))}
                    {!dealers.length && (
                        <Table.Tr><Table.Td colSpan={5}><Text c="dimmed" size="sm">
                            {loading ? "Loading…"
                                : !groupIds.length ? "No dealer groups configured (ctc_config/administration.administrationGroupId)."
                                    : term ? "No dealers match your search."
                                        : "No staff in the dealer groups have signed in since group sync was enabled."}
                        </Text></Table.Td></Table.Tr>
                    )}
                </Table.Tbody>
            </Table>
        </Stack>
    );
};

export default CtcDealers;